import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost, formatDateTime, formatLKR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import SignalBadge from "@/components/common/SignalBadge";
import ScoreGauge from "@/components/common/ScoreGauge";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import WatchlistStarButton from "@/components/common/WatchlistStarButton";
import ShareTypePill from "@/components/stocks/ShareTypePill";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, RefreshCcw, Search, ChevronRight, Sparkles, Loader2, Zap, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { LockedFeature } from "@/components/common/Upgrade";

const SIGNALS = ["all", "STRONG_BUY", "BUY", "WATCH", "HOLD", "AVOID"];
const SIGNAL_LABELS = {
  all: "All signals",
  STRONG_BUY: "STRONG BUY only",
  BUY: "BUY only",
  WATCH: "WATCH only",
  HOLD: "HOLD only",
  AVOID: "AVOID only",
};
const SORT_OPTIONS = [
  { value: "score_desc", label: "Score: High to Low" },
  { value: "score_asc",  label: "Score: Low to High" },
  { value: "recent",     label: "Recently Updated" },
  { value: "alpha",      label: "Alphabetical" },
];

const AiInsightsPage = () => {
  const { isPremium, isAdmin } = useAuth();
  const [coverage, setCoverage] = useState({ analysed: [], unanalysed: [], analysed_count: 0, unanalysed_count: 0, total_companies: 0 });
  const [loading, setLoading] = useState(isPremium);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [signalFilter, setSignalFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("score_desc");
  const [refreshing, setRefreshing] = useState(false);
  const [activeShareType, setActiveShareType] = useState({});
  const [generating, setGenerating] = useState({});
  const [unanalysedSearch, setUnanalysedSearch] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [hideTarget, setHideTarget] = useState(null);
  const [hiding, setHiding] = useState(false);

  const load = useCallback(() => {
    if (!isPremium) return;
    setLoading(true);
    apiGet(`/ai-analysis/coverage?show_hidden=${showHidden ? "true" : "false"}`)
      .then(setCoverage)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [isPremium, showHidden]);

  const loadProgress = useCallback(() => {
    if (!isPremium) return;
    apiGet("/ai-analysis/progress").then((p) => {
      setProgress(p);
      if (p?.status === "running") setBulkRunning(true);
      if (p?.status === "completed" && bulkRunning) {
        setBulkRunning(false);
        toast.success(`Bulk analysis complete — ${p.succeeded} succeeded, ${p.failed} failed.`);
        load();
      }
    }).catch(() => {});
  }, [isPremium, bulkRunning, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadProgress();
    const id = setInterval(loadProgress, 5000);
    return () => clearInterval(id);
  }, [loadProgress]);

  const filteredAnalysed = useMemo(() => {
    let out = [...(coverage.analysed || [])];
    if (showHidden) out = out.concat(coverage.hidden || []);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (a) => a.company_name?.toLowerCase().includes(q) || a.company_code.toLowerCase().includes(q),
      );
    }
    if (signalFilter !== "all") {
      // Strict composite-signal match. Mutually exclusive: a stock with
      // composite_signal==='BUY' will NEVER appear under the WATCH filter
      // even if its legacy AI signal happens to be WATCH.
      out = out.filter((a) =>
        a.securities_analysis?.some(
          (s) => (s.composite_signal || "").toUpperCase() === signalFilter,
        ),
      );
    }
    // Composite-score sorting: pick the highest score across the company's
    // securities (voting + non-voting) so the same company doesn't bounce
    // around when an admin toggles share class.
    const _topScore = (a) => {
      const scores = (a.securities_analysis || [])
        .map((s) => Number(s.composite_score ?? s.score))
        .filter((n) => Number.isFinite(n));
      return scores.length ? Math.max(...scores) : -1;
    };
    const _updated = (a) => {
      const ts = (a.securities_analysis || [])
        .map((s) => s.updated_at || s.last_analysed_at || s.analysed_at)
        .filter(Boolean)
        .sort()
        .pop();
      return ts ? new Date(ts).getTime() : 0;
    };
    if (sortOrder === "score_desc") {
      out.sort((a, b) => _topScore(b) - _topScore(a));
    } else if (sortOrder === "score_asc") {
      out.sort((a, b) => _topScore(a) - _topScore(b));
    } else if (sortOrder === "recent") {
      out.sort((a, b) => _updated(b) - _updated(a));
    } else if (sortOrder === "alpha") {
      out.sort((a, b) =>
        (a.company_name || a.company_code || "").localeCompare(b.company_name || b.company_code || "")
      );
    }
    return out;
  }, [coverage.analysed, coverage.hidden, search, signalFilter, sortOrder, showHidden]);

  const filteredUnanalysed = useMemo(() => {
    let out = coverage.unanalysed || [];
    if (unanalysedSearch.trim()) {
      const q = unanalysedSearch.trim().toLowerCase();
      out = out.filter(
        (u) => u.company_name?.toLowerCase().includes(q) || u.company_code.toLowerCase().includes(q),
      );
    }
    return out;
  }, [coverage.unanalysed, unanalysedSearch]);

  if (!isPremium) {
    return (
      <LockedFeature
        feature="ai-insights"
        title="AI Insights is a Premium feature"
        blurb="See Claude's BUY / WATCH / HOLD signals, score gauges, strengths and risks for every company on your watchlist. Available with the free 14-day Premium trial."
        preview={
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="border bg-card">
                <CardContent className="p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="h-4 w-24 rounded bg-muted" />
                    <div className="h-6 w-14 rounded bg-muted" />
                  </div>
                  <div className="h-3 w-full rounded bg-muted" />
                  <div className="h-3 w-5/6 rounded bg-muted" />
                </CardContent>
              </Card>
            ))}
          </div>
        }
      />
    );
  }

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await apiPost("/ai-analysis/refresh-all");
      toast.success("Refresh started in background.");
      setTimeout(load, 8000);
    } catch (e) {
      toast.error("Failed to start refresh");
    } finally { setRefreshing(false); }
  };

  const generateOne = async (code, name) => {
    setGenerating((g) => ({ ...g, [code]: true }));
    try {
      // Unhide if it was hidden, so it shows in the dashboard again
      try { await apiPost(`/ai-analysis/${code}/unhide`); } catch (e) { /* ignore 404 */ }
      await apiPost(`/ai-analysis/${code}/refresh`);
      toast.success(`Analysis generated for ${name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to analyse ${name}`);
    } finally {
      setGenerating((g) => ({ ...g, [code]: false }));
    }
  };

  const confirmHide = async () => {
    if (!hideTarget) return;
    setHiding(true);
    try {
      await apiPost(`/ai-analysis/${hideTarget.company_code}/hide`);
      toast.success(`${hideTarget.company_name} hidden from AI Insights`);
      setHideTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to hide card");
    } finally { setHiding(false); }
  };

  const unhideOne = async (code, name) => {
    try {
      await apiPost(`/ai-analysis/${code}/unhide`);
      toast.success(`${name} restored to dashboard`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore card");
    }
  };

  const startBulk = async () => {
    setBulkRunning(true);
    setBulkOpen(false);
    try {
      const res = await apiPost("/ai-analysis/analyse-all-unanalysed");
      if (res.status === "already_running") {
        toast.info("A bulk analysis job is already running.");
      } else {
        toast.success("Bulk analysis started — progress will update live.");
      }
      loadProgress();
    } catch (e) {
      setBulkRunning(false);
      toast.error(e?.response?.data?.detail || "Failed to start bulk analysis");
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  const total = coverage.total_companies || 0;
  const analysedCount = coverage.analysed_count || 0;
  const pct = total ? Math.round((analysedCount / total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="ai-insights-page">
      {/* Coverage header */}
      <Card className="border bg-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-lg" data-testid="ai-coverage-counter">
                  {analysedCount} of {total} stocks analysed
                  <span className="ml-2 text-sm font-normal text-muted-foreground tabular">({pct}%)</span>
                </div>
                <p className="text-sm text-muted-foreground truncate">
                  Run Claude analysis individually or in bulk. New stocks need analysis before they appear in the cards above.
                </p>
                <div className="mt-2 h-1.5 w-full max-w-md rounded-full bg-muted overflow-hidden" data-testid="ai-coverage-bar">
                  <div
                    className="h-full bg-[hsl(var(--primary))] transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={refreshAll} disabled={refreshing} variant="outline" data-testid="ai-refresh-existing" className="gap-2">
                <RefreshCcw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh existing
              </Button>
              {isAdmin && coverage.unanalysed_count > 0 && (
                <Button
                  onClick={() => setBulkOpen(true)}
                  disabled={bulkRunning}
                  className="gap-2 bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/90 text-white"
                  data-testid="ai-analyse-all-admin"
                >
                  {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  Analyse All Stocks (Admin)
                </Button>
              )}
            </div>
          </div>
          <Progress value={pct} className="h-2" data-testid="ai-coverage-progress" />
          <div className="text-xs text-muted-foreground">{pct}% complete</div>

          {bulkRunning && progress?.status === "running" && (
            <div className="rounded-md border bg-[hsl(var(--info))]/5 px-3 py-2 text-xs flex items-center gap-3" data-testid="ai-bulk-progress">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[hsl(var(--info))]" />
              <span className="font-medium">Bulk analysis in progress:</span>
              <span className="text-muted-foreground">
                {progress.processed}/{progress.total} processed · {progress.succeeded} succeeded · {progress.failed} failed
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ----------- Section 1 — analysed companies ----------- */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="font-display text-base font-semibold">Analysed companies ({analysedCount})</h2>
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            {coverage.hidden_count > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 h-9 text-xs"
                onClick={() => setShowHidden((s) => !s)}
                data-testid="ai-show-hidden-toggle"
              >
                {showHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showHidden ? "Hide" : "Show"} hidden ({coverage.hidden_count})
              </Button>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search analysed…"
                className="pl-9 w-56 h-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="ai-search-input"
              />
            </div>
            <Select value={signalFilter} onValueChange={setSignalFilter}>
              <SelectTrigger className="w-36 h-9 text-xs" data-testid="ai-signal-filter">
                <SelectValue placeholder="Signal" />
              </SelectTrigger>
              <SelectContent>
                {SIGNALS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`ai-signal-option-${s}`}>
                    {SIGNAL_LABELS[s] || s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-44 h-9 text-xs" data-testid="ai-sort-order">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`ai-sort-option-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {filteredAnalysed.length === 0 ? (
          <Card className="border bg-card">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No analysed companies match. Try clearing filters or running an analysis below.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ai-insights-grid">
            {filteredAnalysed.map((a) => {
              const activeSym = activeShareType[a.company_code] || a.securities_analysis?.[0]?.cse_symbol;
              const active = a.securities_analysis?.find((s) => s.cse_symbol === activeSym) || a.securities_analysis?.[0];
              return (
                <Card key={a.company_code} className="border bg-card flex flex-col" data-testid={`ai-card-${a.company_code}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="font-display text-base flex items-center gap-2 min-w-0">
                        <Bot className="h-4 w-4 text-primary shrink-0" />
                        <CompanyLink
                          companyCode={a.company_code}
                          name={a.company_name}
                          fromLabel="AI Insights"
                          className="truncate"
                          testId={`ai-name-${a.company_code}`}
                        />
                      </CardTitle>
                      <div className="flex items-center gap-1.5">
                        {active && <WatchlistStarButton cseSymbol={active.cse_symbol} companyName={a.company_name} />}
                        {/* Composite signal — same one shown on the screener.
                           Falls back to AI signal if composite hasn't been computed yet. */}
                        {active && (
                          <SignalBadge signal={active.composite_signal || active.ai_signal || active.signal} />
                        )}
                        {a.hidden_from_insights ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => unhideOne(a.company_code, a.company_name)}
                            title="Restore to dashboard"
                            data-testid={`ai-unhide-${a.company_code}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-[hsl(var(--destructive))]"
                            onClick={() => setHideTarget(a)}
                            title="Remove from AI Insights dashboard"
                            data-testid={`ai-hide-${a.company_code}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {active && (
                        <SymbolLink
                          companyCode={a.company_code}
                          symbol={active.cse_symbol}
                          fromLabel="AI Insights"
                          testId={`ai-symbol-${active.cse_symbol}`}
                        />
                      )}{" "}
                      · Updated {formatDateTime(a.analysis_date)}
                    </p>
                  </CardHeader>
                  <CardContent className="flex-1 space-y-3">
                    {a.securities_analysis?.length > 1 && (
                      <div className="flex gap-1.5">
                        {a.securities_analysis.map((sa) => (
                          <ShareTypePill
                            key={sa.cse_symbol}
                            shareType={sa.share_type}
                            active={sa.cse_symbol === activeSym}
                            onClick={() => setActiveShareType((m) => ({ ...m, [a.company_code]: sa.cse_symbol }))}
                            size="sm"
                          />
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      {active && (
                        <ScoreGauge
                          score={active.composite_score ?? active.score ?? 0}
                          label="SCORE"
                          data-testid={`ai-score-gauge-${a.company_code}`}
                        />
                      )}
                      <p className="text-sm text-muted-foreground line-clamp-4">{active?.rationale}</p>
                    </div>

                    {/* Score legend — composite (sortable, same as screener) + AI signal */}
                    {active && (
                      <div className="flex items-center justify-between gap-2 rounded-md bg-muted/30 border border-border/60 px-2.5 py-1.5 text-[11px]">
                        <div className="flex items-center gap-1.5" data-testid={`ai-card-composite-${a.company_code}`}>
                          <span className="text-muted-foreground uppercase tracking-wider text-[9.5px]">Composite</span>
                          <span className="font-display font-semibold tabular text-foreground">
                            {active.composite_score ?? "—"}
                          </span>
                          {(active.composite_signal || active.signal) && (
                            <SignalBadge
                              signal={active.composite_signal || active.signal}
                              className="text-[9px] px-1 py-0"
                            />
                          )}
                        </div>
                        <span className="text-muted-foreground/50">|</span>
                        <div className="flex items-center gap-1.5" data-testid={`ai-card-ai-${a.company_code}`}>
                          <span className="text-muted-foreground uppercase tracking-wider text-[9.5px]">AI signal</span>
                          <span className="font-display font-semibold tabular text-foreground">
                            {active.ai_score ?? active.score ?? "—"}
                          </span>
                          {(active.ai_signal || active.signal) && (
                            <SignalBadge
                              signal={active.ai_signal || active.signal}
                              className="text-[9px] px-1 py-0"
                            />
                          )}
                        </div>
                      </div>
                    )}

                    <p className="text-sm leading-relaxed text-foreground/90 line-clamp-3">{a.company_summary}</p>
                  </CardContent>
                  <div className="px-5 pb-5">
                    <Link
                      to={`/stocks/${a.company_code}`}
                      state={{ from: { pathname: "/ai-insights", label: "AI Insights" } }}
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      Open detail <ChevronRight className="h-3 w-3" />
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ----------- Section 2 — unanalysed companies ----------- */}
      {coverage.unanalysed_count > 0 && (
        <div data-testid="ai-unanalysed-section">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground border-border">No Data</Badge>
              <h2 className="font-display text-base font-semibold">
                Stocks without AI Analysis ({coverage.unanalysed_count})
              </h2>
            </div>
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search unanalysed…"
                className="pl-9 w-56 h-9"
                value={unanalysedSearch}
                onChange={(e) => setUnanalysedSearch(e.target.value)}
                data-testid="ai-unanalysed-search"
              />
            </div>
          </div>

          <Card className="border bg-card overflow-hidden">
            <div className="overflow-x-auto max-h-[480px]">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 sticky top-0">
                  <tr className="border-b">
                    <th className="px-4 py-2.5 text-xs uppercase tracking-wide text-muted-foreground text-right w-12">#</th>
                    <th className="px-4 py-2.5 text-xs uppercase tracking-wide text-muted-foreground text-left">Company</th>
                    <th className="px-4 py-2.5 text-xs uppercase tracking-wide text-muted-foreground text-left">Sector</th>
                    <th className="px-4 py-2.5 text-xs uppercase tracking-wide text-muted-foreground text-right">Last price</th>
                    <th className="px-4 py-2.5 text-xs uppercase tracking-wide text-muted-foreground text-center">Signal</th>
                    <th className="px-4 py-2.5 text-xs uppercase tracking-wide text-muted-foreground text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUnanalysed.map((u, idx) => (
                    <tr key={u.company_code} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-right text-xs text-muted-foreground tabular">{idx + 1}</td>
                      <td className="px-4 py-2.5">
                        <CompanyLink
                          companyCode={u.company_code}
                          name={u.company_name}
                          fromLabel="AI Insights"
                          className="font-medium text-sm"
                        />
                        <div className="text-[11px] text-muted-foreground font-mono">{u.primary_symbol}</div>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{u.sector || "—"}</td>
                      <td className="px-4 py-2.5 text-right tabular">{formatLKR(u.latest_price)}</td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground border-border">
                          No Data
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateOne(u.company_code, u.company_name)}
                          disabled={!!generating[u.company_code]}
                          className="gap-1.5 h-7 text-xs"
                          data-testid={`ai-generate-${u.company_code}`}
                        >
                          {generating[u.company_code]
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Sparkles className="h-3 w-3" />}
                          Generate Analysis
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredUnanalysed.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                        No matches.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <AlertDialog open={!!hideTarget} onOpenChange={(o) => !o && !hiding && setHideTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <X className="h-4 w-4 text-[hsl(var(--destructive))]" />
              Remove {hideTarget?.company_name} from your AI Insights dashboard?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The analysis data will be kept but the card will be hidden. You can restore it
              by clicking <em>Show hidden</em> at the top of the page or by regenerating the analysis.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={hiding}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmHide}
              disabled={hiding}
              className="bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-white"
              data-testid="ai-hide-confirm"
            >
              {hiding && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Remove from dashboard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[hsl(var(--warning))]" />
              Analyse all {coverage.unanalysed_count} unanalysed stocks?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will use significant AI tokens to analyse all {coverage.unanalysed_count} stocks
              that don't have analysis yet. The job runs in the background and may take 20–30 minutes
              to finish. You can keep using the app while it runs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={startBulk}
              className="bg-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/90 text-white"
              data-testid="ai-analyse-all-confirm"
            >
              Yes, analyse all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AiInsightsPage;
